import React, { useEffect, useState } from "react";
import {
  Button,
  Choice,
  Input,
  Select,
  Link,
  Progress,
  Text,
} from "@boulevard/blvd-ui";
import "@boulevard/blvd-ui/index.css";
import * as Comlink from "comlink";
import "./App.css";
import {
  OKTA_GATE,
  UPDATE_OKTA_GATE
} from "./graph";

interface OktaGate {
  ssoEnabled: boolean;
  clientSecretProvided: boolean;
  clientId?: string;
  clientSecret?: string;
  orgName?: string;
  reauthenticationDuration?: string;
  initiateUrl?: string;
  callbackUrl?: string;
  logoutUrl?: string;
}

const REAUTHENTICATION_DURATIONS = [
  { label: '1 hour', value: 'P1H' },
  { label: '4 hours', value: 'P4H' },
  { label: '8 hours', value: 'P8H' },
  { label: '12 hours', value: 'P12H' },
  { label: '1 day', value: 'P1D' },
  { label: '1 week', value: 'P7D' }
];

interface UpdateOktaGateInput {
  ssoEnabled?: boolean;
  clientId?: string;
  clientSecret?: string;
  orgName?: string;
  reauthenticationDuration?: string;
}

const api: any = Comlink.wrap(Comlink.windowEndpoint(window.parent));

// The proxy must be released on window unload rather than
// a lifecycle within React because this app runs inside of
// an iframe which can be destroyed from the outside
window.addEventListener("unload", api[Comlink.releaseProxy]);

function App() {
  const [oktaGate, setOktaGate] = useState<OktaGate>();
  const [clientSecretOverride, setClientSecretOverride] = useState<Boolean>(false);
  const [showSaveSuccess, setSuccess] = useState<Boolean>(false);

  const save = async () => {
    const { ssoEnabled, clientId, clientSecret, orgName, reauthenticationDuration } = oktaGate!;

    const input: UpdateOktaGateInput = {
      ssoEnabled,
      clientId,
      orgName,
      reauthenticationDuration
    };

    if (!oktaGate!.clientSecretProvided || clientSecretOverride) {
      // Only set the `clientSecret` if it hasn't been provided already or we're overriding.
      input.clientSecret = clientSecret;
    }

    const { data: { updateOktaGate: { oktaGate: updatedOktaGate } } } = await api.submitRequest({ query: UPDATE_OKTA_GATE, variables: { input } });
    setOktaGate(updatedOktaGate);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2500);
    // api.appConfigContext.onClose();
  };

  useEffect(() => {
    (async () => {
      const { data: { oktaGate } } = await api.submitRequest({ query: OKTA_GATE });
      setOktaGate(oktaGate);
    })();
  }, []);

  if (!oktaGate) {
    return (
      <div className="okta-sso-wrapper">
        <Progress variant="indeterminate" />
      </div>
    )
  }

  const reauthenticationDurationValue = REAUTHENTICATION_DURATIONS.find((o) => o.value === oktaGate.reauthenticationDuration);

  const onChangeSSO = (event: any) => {
    setOktaGate({ ...oktaGate, ssoEnabled: event.currentTarget.checked! });
  }

  const renderConfiguration = () => {
    return (<>
      <div>
        <label htmlFor="name" style={{ minWidth: 100 }}>
          Okta Organization Subdomain
      </label>
        <div className="pb-8">
          <Input
            id="oktaSubdomain"
            value={oktaGate.orgName}
            onChange={event =>
              setOktaGate({
                ...oktaGate,
                orgName: event.currentTarget.value
              })
            }
          />
        </div>
      </div>

      <div className="pb-8">
        <label htmlFor="name" style={{ minWidth: 100 }}>
          Okta Client Id
      </label>
        <div>
          <Input
            id="clientId"
            value={oktaGate.clientId}
            onChange={event =>
              setOktaGate({
                ...oktaGate,
                clientId: event.currentTarget.value
              })
            }
          />
        </div>
      </div>

      <div className="pb-8">
        <label htmlFor="name" style={{ minWidth: 100 }}>
          Okta Client Secret
        </label>
        <div>
          <Input
            id="clientSecret"
            type="password"
            disabled={oktaGate.clientSecretProvided && !clientSecretOverride}
            value={oktaGate.clientSecret}
            placeholder={oktaGate.clientSecretProvided ? '•••••••' : ''}
            onChange={event =>
              setOktaGate({
                ...oktaGate,
                clientSecret: event.currentTarget.value
              })
            }
          />

          {!clientSecretOverride && (
            <Text>Secret already provided. <Link href="#" onClick={() => setClientSecretOverride(true)}>Clear and re-enter</Link></Text>
          )}

        </div>
      </div>

      <div className="pb-8">
        <label htmlFor="name" style={{ minWidth: 100 }}>
          Re-authentication duration
      </label>
        <div>
          <Select
            appearance="searchable-input"
            closeMenuOnSelect={false}
            menuPlacement="bottom"
            menuPosition="fixed"
            onChange={(option: any) => {
              setOktaGate({
                ...oktaGate,
                reauthenticationDuration: option.value
              })
            }}
            options={REAUTHENTICATION_DURATIONS}
            portal
            size="xsmall"
            value={reauthenticationDurationValue}
          />
        </div>
      </div>
    </>);
  };

  return (
    <div className="okta-sso-wrapper">
      <div className="okta-sso-body">
        <Text as="h5" variant="h5" className="heading heading-underline">
          Configure Okta SSO
        </Text>

        <Text as="p" variant="body3">
          Provide the credentials from your Okta application.
        </Text>

        <form autoComplete="off">
          <div>
            <label>
              <div className="pb-8">
                <Choice
                  checked={oktaGate.ssoEnabled}
                  onChange={onChangeSSO}
                  size="small"
                  type="checkbox"
                />

                <span>
                  Enable Okta SSO
                </span>
              </div>
            </label>

            {oktaGate.ssoEnabled && renderConfiguration()}
          </div>
        </form>

        {oktaGate.initiateUrl && (
          <>
            <Text>Use the following URLs for configuration within Okta:</Text>

            <ul>
              <li>Initiate Url: <Text>{oktaGate.initiateUrl}</Text></li>
              <li>Callback Url: <Text>{oktaGate.callbackUrl}</Text></li>
              <li>Logout Url: <Text>{oktaGate.logoutUrl}</Text></li>
            </ul>
          </>
        )}


      </div>

      <div className="okta-sso-footer">
        <Button onClick={save} className={showSaveSuccess && 'success'} disabled={showSaveSuccess ? true : undefined}>
          {showSaveSuccess ? 'Saved!' : 'Save'}
        </Button>
      </div>
    </div>
  );
}


export default App;
